import { apolloClient } from "@/vue-apollo.js";
import {
  renderHtmlFromBuilderJson,
  getBrandTokens,
  applyBrandDefaultsToModule,
} from "@/utils/mail.js";
import { COMPONENT_NAME_MAP, BUILDER_ELEMENTS } from "./Constants.js";
import { PUT_DATOS_MARCA, SAVE_MAIL, CREATE_AUTOMATED_TEMPLATE } from "@/apollo/mutations/mail";
import { SAVE_FILE_MAIL_BUILDER_BODY } from "@/apollo/subscriptions.js";
import {
  GET_DATOS_MARCA,
  GET_FONTS,
  LIST_LISTAS_CONTACTOS,
  GET_LIST_REMITENTES_EMPRESA,
  FIND_CORREO_BY_ID,
  GET_CATEGORIAS_MAIL,
  GET_TEMPLATES_BY_CATEGORIA,
} from "@/apollo/queries/mail";
import { exMut, exQue } from "@/utils/gql";
import { generateId } from "@/utils/id.js";
import { mapTemplatesToForm } from "./recursos.js";

export const mapCorreoToForm = (correo, direcciones = []) => {
  if (!correo) return {};

  const direccion = correo.remitente
    ? {
      id: correo.remitente.id_remitente,
      text: correo.remitente.correo_remitente,
      nombre:
        correo.remitente.nombre_remitent ||
        correo.remitente.nombre_remitente ||
        "",
    }
    : null;

  const direccionMatch = direccion
    ? direcciones.find((d) => Number(d.id) === Number(direccion.id)) ||
    direccion
    : null;

  const destinatariosUsuarios = (Array.isArray(correo.destinatarios) ? correo.destinatarios : [])
    .filter((d) => d && d.usuario)
    .map((d) => {
      const u = d.usuario;
      const nombres = [
        u.primer_nombre,
        u.segundo_nombre,
        u.apellido_paterno,
        u.apellido_materno,
      ]
        .filter(Boolean)
        .join(" ");
      return {
        id: u.id_usuario,
        correo: u.correo,
        label: nombres
          ? `${nombres}${u.correo ? ` (${u.correo})` : ""}`
          : u.correo,
        tipo: "usuario",
      };
    });

  const destinatariosListas = (Array.isArray(correo.destinatarios) ? correo.destinatarios : [])
    .filter((d) => d && d.lista)
    .map((d) => {
      const l = d.lista;
      return {
        id: l.id_lista,
        text: l.nombre_lista,
        nombre_lista: l.nombre_lista,
        tipo: "lista",
        correos_excluidos: d.correos_excluidos || [],
        id_destinatario: d.id_destinatario,
      };
    });

  // Mapear archivos adjuntos del formato backend al formato del store
  const archivosAdjuntos = (correo.adjuntos || []).map((adj) => ({
    id_archivo: adj.id_adjunto,
    url: adj.url_adjunto || "",
    nombre: adj.nombre_adjunto || "",
    size: Number(adj.size) || 0,
    type: adj.formato || "",
  }));

  const fechaEnvio = correo?.fecha_envio || "";
  const timePart = fechaEnvio ? fechaEnvio.split("T")[1] : "";
  const hour = timePart ? timePart.slice(0, 5) : "";
  const date = fechaEnvio ? fechaEnvio.split("T")[0] : "";

  return {
    id_correo: correo.id_correo,
    direccion: direccionMatch,
    remitente: correo.nombre_remitente || direccionMatch?.nombre || "",
    asunto: correo.asunto || "",
    introduccion: correo.introduccion || "",
    id_categoria_fk: correo.categoria?.id_categoria || null,
    destinatarios: destinatariosUsuarios,
    destinatariosListasSeleccionadas: destinatariosListas,
    archivosAdjuntos,
    htmlMail: correo.contenido_html || "",
    jsonMail: correo.contenido_json
      ? JSON.parse(correo.contenido_json)
      : undefined,
    fecha: date,
    hora: hour,
    programar: Boolean(correo.fecha_envio)
  };
};

const getInitialSection = () => {
  const stored = localStorage.getItem("mail-create-section");
  return stored ? JSON.parse(stored) : 0;
};

const getStoredForm = () => {
  const stored = localStorage.getItem("mail-create-form");
  return stored ? JSON.parse(stored) : null;
};
const persistFormToStorage = (form) => {
  const safeForm = {
    ...(form || {}),
    archivosAdjuntos: [],
  };
  localStorage.setItem("mail-create-form", JSON.stringify(safeForm));
};
export { generateId };

const normalizeSocialContent = (content = []) => {
  if (!Array.isArray(content)) return [];
  return content.map((item) => {
    if (!item || typeof item !== "object") return item;
    return {
      ...item,
      color: item.color || "",
    };
  });
};

export const normalizeModule = (module) => {
  if (!module || typeof module !== "object") {
    return null;
  }
  const legacyName = module.component || module.type || "";
  const normalizedComponent = COMPONENT_NAME_MAP[legacyName] || legacyName;

  // Sin componente resoluble esto no es un módulo: o es una fila que acabó
  // en `cols` por un drag mal dirigido, o un resto ya corrupto. Antes se
  // devolvía igual y, con el `delete moduleClone.cols` de abajo, quedaba un
  // `{id, component: ""}` que no renderiza nada: el módulo "desaparecía".
  // Devolver null hace que `normalizeRows` lo descarte con `filter(Boolean)`,
  // lo que además limpia los fantasmas ya persistidos.
  if (!normalizedComponent) {
    return null;
  }

  const moduleClone = JSON.parse(JSON.stringify(module));
  delete moduleClone.cols;
  return {
    ...moduleClone,
    component: normalizedComponent,
    content:
      moduleClone.type === "Social" || normalizedComponent === "MailSocialModule"
        ? normalizeSocialContent(moduleClone.content)
        : moduleClone.content,
    id: moduleClone.id ?? generateId(),
  };
};

const getBrandTokensSafe = (datosMarca) => getBrandTokens(datosMarca || {});

const applyBrandDefaults = (module, brandTokens) => {
  const normalized = normalizeModule(module);
  if (!normalized) return null;
  if (!brandTokens) return normalized;
  return {
    ...normalized,
    styles: applyBrandDefaultsToModule(normalized, brandTokens),
  };
};

const buildHtmlCtaContent = (brandTokens) => {
  const primary = brandTokens?.primaryColor || "#000000";
  const secondary = brandTokens?.secondaryColor || primary;
  return `<h2 style="color: ${primary};">Título</h2><p style="color: ${secondary};">Parrafo</p><a href="#" style="display:inline-block;padding:10px 16px;border-radius:16px;border:2px solid ${primary};color:${primary};text-decoration:none;">Botón</a>`;
};

const normalizeRows = (rows = []) => {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map((row) => {
    if (row && Array.isArray(row.cols)) {
      const normalizedCols = (row.cols || [])
        .map((col) => normalizeModule(col))
        .filter(Boolean);
      return {
        ...row,
        id: row.id ?? generateId(),
        cols: normalizedCols,
      };
    }
    const module = normalizeModule(row);
    return {
      id: row?.id ?? generateId(),
      cols: module ? [module] : [],
    };
  });
};

const ensureRows = (rows = []) => {
  const normalized = normalizeRows(rows);
  // Remove duplicate module IDs across all rows
  const seenIds = new Set();
  for (const row of normalized) {
    if (!row.cols) continue;
    // Iterate backwards to safely splice
    for (let i = row.cols.length - 1; i >= 0; i--) {
      const col = row.cols[i];
      if (col && col.id != null) {
        if (seenIds.has(col.id)) {
          // Duplicate found, remove it
          row.cols.splice(i, 1);
        } else {
          seenIds.add(col.id);
        }
      }
    }
  }
  return normalized.length
    ? normalized
    : [
      {
        id: generateId(),
        cols: [],
      },
    ];
};

/**
 * Resuelve la posición de un módulo dentro de jsonMail.rows.
 *
 * Acepta dos formas de direccionamiento:
 *  - `moduleId`  → preferido: sobrevive a reordenamientos, borrados y undo.
 *  - `{rowIndex, modIndex}` → heredado; queda por compatibilidad, pero apunta
 *    a *posiciones*, así que tras un reorder puede referirse a otro módulo.
 *
 * Devuelve `{ rowIndex, modIndex, module }` o `null` si no existe.
 */
const locateModule = (state, { moduleId, rowIndex, modIndex } = {}) => {
  const rows = state.form?.jsonMail?.rows || [];

  if (moduleId != null) {
    for (let r = 0; r < rows.length; r++) {
      const cols = rows[r]?.cols || [];
      for (let c = 0; c < cols.length; c++) {
        if (cols[c] && cols[c].id === moduleId) {
          return { rowIndex: r, modIndex: c, module: cols[c] };
        }
      }
    }
    return null;
  }

  const module = rows[rowIndex]?.cols?.[modIndex];
  return module ? { rowIndex, modIndex, module } : null;
};

/**
 * Refleja en `selectedModule` un cambio hecho sobre un módulo del canvas,
 * pero SOLO si se trata del mismo módulo.
 *
 * Antes se copiaba siempre, sin comprobar de qué módulo venía el cambio: si
 * había un módulo seleccionado y se editaba otro (p.ej. escribir en un texto
 * que no era el seleccionado), el panel de estilos terminaba mostrando datos
 * del módulo equivocado.
 */
const syncSelectedModule = (state, module, patch) => {
  const selected = state.selectedModule && state.selectedModule.module;
  if (!selected) return;
  // El "módulo" sintético del correo completo (selectMail) no tiene id.
  if (module.id == null || selected.id !== module.id) return;
  Object.assign(selected, patch);
};

/**
 * Reemplaza las filas del correo por `rows`. Es el mismo camino que
 * `setJsonMailRows`: pasa por `ensureRows` (normaliza y descarta ids repetidos)
 * y reemplaza `state.form`, así que todo lo que lee `form` se entera. Lo usan
 * las mutaciones de estructura por id (agregar, mover y duplicar sin arrastrar),
 * que no pasan por Sortable.
 */
const reemplazarFilas = (state, rows) => {
  state.form = {
    ...state.form,
    jsonMail: {
      ...state.form.jsonMail,
      rows: ensureRows(rows),
    },
  };
  persistFormToStorage(state.form);
};

/**
 * Deja seleccionado el bloque `moduleId` y avisa al canvas de que es nuevo
 * (`ultimoBloqueAgregadoId`, que DesignBuilder usa para llevarlo a la vista).
 *
 * Que la selección siga al bloque nuevo es lo que permite armar un correo de
 * arriba hacia abajo a puro clic: el siguiente "agregar" cae debajo de él. La
 * copia es profunda por lo mismo que en `selectModule` (DesignBuilder): el
 * panel no debe compartir objeto con el módulo del canvas.
 */
const seleccionarBloqueNuevo = (state, moduleId) => {
  const found = locateModule(state, { moduleId });
  if (!found) return;
  state.selectedModule = {
    rowIndex: found.rowIndex,
    modIndex: found.modIndex,
    module: JSON.parse(JSON.stringify(found.module)),
  };
  state.ultimoBloqueAgregadoId = moduleId;
};

/** Copia de una fila con ids nuevos (la fila y cada bloque), lista para insertar. */
const clonarFila = (row) => {
  const copia = JSON.parse(JSON.stringify(row));
  return {
    ...copia,
    id: generateId(),
    cols: (copia.cols || []).map((col) => ({ ...col, id: generateId() })),
  };
};

/**
 * Inserta filas ya armadas (un bloque en su propia fila, o las de un diseño
 * prearmado) sin arrastrar. Dónde caen:
 *  - Con `despuesDeId` (id de un bloque): en filas nuevas justo debajo de la
 *    fila que lo contiene, aunque esa fila tenga varias columnas.
 *  - Sin él (no hay selección, o el id ya no existe): al final. Si la última
 *    fila está vacía se ocupa en vez de dejarla arriba, huérfana: es el caso del
 *    correo recién creado, que parte con una fila vacía.
 * Deja seleccionado el último bloque insertado.
 */
const insertarFilasEn = (state, filas, despuesDeId) => {
  const nuevas = (filas || []).filter(Boolean);
  if (!nuevas.length) return;

  const rows = state.form?.jsonMail?.rows || [];
  const ancla =
    despuesDeId != null ? locateModule(state, { moduleId: despuesDeId }) : null;
  const siguientes = [...rows];
  if (ancla) {
    siguientes.splice(ancla.rowIndex + 1, 0, ...nuevas);
  } else {
    const ultima = rows[rows.length - 1];
    const ultimaVacia = Boolean(ultima) && !(ultima.cols && ultima.cols.length);
    siguientes.splice(
      ultimaVacia ? rows.length - 1 : rows.length,
      ultimaVacia ? 1 : 0,
      ...nuevas,
    );
  }
  reemplazarFilas(state, siguientes);

  for (let i = nuevas.length - 1; i >= 0; i--) {
    const cols = nuevas[i].cols || [];
    if (cols.length) {
      seleccionarBloqueNuevo(state, cols[cols.length - 1].id);
      break;
    }
  }
};

const builderElementMap = BUILDER_ELEMENTS.reduce((acc, element) => {
  acc[element.name] = element;
  return acc;
}, {});

const cloneBuilderElement = (name, overrides = {}, brandTokens = null) => {
  const base = builderElementMap[name];
  if (!base) return null;
  const cloned = JSON.parse(JSON.stringify(base));
  return applyBrandDefaults(
    {
      ...cloned,
      ...overrides,
    },
    brandTokens,
  );
};

const createSectionRow = (cols = [], brandTokens = null) => ({
  id: generateId(),
  cols: (cols || [])
    .map((col) => applyBrandDefaults(col, brandTokens))
    .filter(Boolean),
});

const buildBuilderSections = (brandTokens = null) => [
  {
    id: "section-vertical-image-title-text-button",
    name: "Imagen / Título / Texto / Botón",
    description: "Diseño prearmado básico, en vertical",
    rows: [
      createSectionRow(
        [cloneBuilderElement("Imagen", {}, brandTokens)],
        brandTokens,
      ),
      createSectionRow(
        [cloneBuilderElement("Título", {}, brandTokens)],
        brandTokens,
      ),
      createSectionRow(
        [cloneBuilderElement("Texto", {}, brandTokens)],
        brandTokens,
      ),
      createSectionRow(
        [cloneBuilderElement("Botón", {}, brandTokens)],
        brandTokens,
      ),
    ],
  },
  {
    id: "section-image-text-two-columns",
    name: "Imagen + Texto (2 columnas)",
    description: "Imagen a la izquierda y texto con botón a la derecha",
    rows: [
      createSectionRow(
        [
          cloneBuilderElement("Imagen", {}, brandTokens),
          cloneBuilderElement(
            "HTML",
            {
              content: buildHtmlCtaContent(brandTokens),
            },
            brandTokens,
          ),
        ],
        brandTokens,
      ),
    ],
  },
];

// Normaliza las listas de destinatarios que puedan venir del storage legadas
const normalizeDestinatariosListasSeleccionadas = (stored = {}) => {
  if (Array.isArray(stored.destinatariosListasSeleccionadas)) {
    return stored.destinatariosListasSeleccionadas;
  }
  if (Array.isArray(stored.destinatariosLista)) {
    return stored.destinatariosLista;
  }
  if (stored.destinatariosLista) {
    return [stored.destinatariosLista];
  }
  return [];
};

const formatDateString = (value) => {
  if (!value) return "";
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === "string") return value;
  return "";
};

/**
 * El backend espera "HH:MM:SS"; el selector de hora entrega "HH:MM".
 * Devuelve "" ante cualquier valor que no sea una hora reconocible: antes se
 * concatenaba a ciegas y un `form.hora` vacío producía "undefined:00".
 */
const formatTimeString = (value) => {
  if (typeof value !== "string") return "";
  const match = value.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return "";
  const [, hora, minutos, segundos = "00"] = match;
  return `${hora.padStart(2, "0")}:${minutos}:${segundos}`;
};

// Arma el payload de destinatarios que espera SaveCorreo
const mapDestinatariosInput = (form = {}) => {
  const usuarios = Array.isArray(form.destinatarios)
    ? form.destinatarios
      .map((u) => u?.id || u?.value || u?.correo)
      .filter(Boolean)
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id))
    : [];

  const listas = Array.isArray(form.destinatariosListasSeleccionadas)
    ? form.destinatariosListasSeleccionadas
      .map((l) => {
        const id = l?.id || l?.id_lista || l?.value;
        if (!id) return null;
        const correosExcluidos = Array.isArray(l?.correos_excluidos)
          ? l.correos_excluidos.filter(Boolean)
          : [];
        return {
          id_lista: Number(id),
          correos_excluidos: correosExcluidos,
        };
      })
      .filter(Boolean)
    : [];

  return {
    destinatarios_usuarios: [...new Set(usuarios)],
    destinatarios_listas: listas,
  };
};

const FORM = {
  id_correo: null,
  direccion: "",
  remitente: "",
  asunto: "",
  itroduccion: "",
  destinatarios: [],
  destinatariosListasSeleccionadas: [],
  archivosAdjuntos: [],
  programar: false,
  fecha: "",
  hora: "",
  automatizar: false,
  automatizacion: {
    id_periodo: null,       // 1=Semanal | 3=Mensual | 7=Anual
    dias_ejecuciones: [],   // Semanal: [1-7] | Mensual: [1-28]
    fechas_ejecuciones: [], // Anual: ["DD-MM"]
    hora: "",               // "HH:MM"
    by_ingreso: false,      // Filtrar por fecha de ingreso
    by_nacimiento: false,   // Filtrar por fecha de nacimiento
    filtros: {
      gerencias: [],
      cargos: [],
      sucursales: [],
      turnos: [],
      sindicatos: [],
    },
  },
  htmlMail: "",
  id_categoria_fk: null,
  datosMarca: {
    colores: [],
    id_marca: null,
    tipo_body: null,
    tipo_header: null,
    url_imagen: "",
  },
  mailContainerStyles: {
    padding: "0",
    display: "flex",
    "align-items": "flex-start",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  mailStyles: {
    width: "600px",
    "border-radius": "0.5rem",
    margin: "0 auto",
    padding: "1rem",
    "max-width": "100%",
  },
  jsonMail: {
    rows: ensureRows([
      {
        id: generateId(),
        cols: [],
      },
    ]), // Array (rows) de objetos con Arrays (columns) de objetos
  },
};

const cloneFormBase = () => JSON.parse(JSON.stringify(FORM));

const buildFormFromStorage = (storedForm) => {
  const base = cloneFormBase();
  if (!storedForm) return base;
  return {
    ...base,
    ...storedForm,
    archivosAdjuntos: [],
    destinatarios: Array.isArray(storedForm.destinatarios)
      ? storedForm.destinatarios
      : [],
    destinatariosListasSeleccionadas:
      normalizeDestinatariosListasSeleccionadas(storedForm),
    destinatariosLista: null,
    mailStyles: { ...base.mailStyles, ...(storedForm.mailStyles || {}) },
    mailContainerStyles: {
      ...base.mailContainerStyles,
      ...(storedForm.mailContainerStyles || {}),
    },
    automatizacion: {
      ...base.automatizacion,
      ...(storedForm.automatizacion || {}),
      by_ingreso: Boolean((storedForm.automatizacion || {}).by_ingreso),
      by_nacimiento: Boolean((storedForm.automatizacion || {}).by_nacimiento),
      filtros: {
        ...base.automatizacion.filtros,
        ...((storedForm.automatizacion || {}).filtros || {}),
      },
    },
    jsonMail: {
      ...base.jsonMail,
      ...(storedForm.jsonMail || {}),
      rows: ensureRows(storedForm.jsonMail && storedForm.jsonMail.rows),
    },
  };
};

const buildBuilderElements = (datosMarca) => {
  const brandTokens = getBrandTokensSafe(datosMarca);
  return BUILDER_ELEMENTS.map((element) => {
    const normalized = applyBrandDefaults(element, brandTokens);
    if (!normalized) return null;
    if (normalized.name === "Logotipo" && datosMarca?.url_imagen) {
      normalized.content = datosMarca.url_imagen;
    }
    return normalized;
  }).filter(Boolean);
};

const buildBuilderSectionsWithBrand = (datosMarca) =>
  buildBuilderSections(getBrandTokensSafe(datosMarca));

const MaxImages = 50;

// Un paso del historial guarda el diseño y los estilos del correo. Antes era
// solo `jsonMail`: cambiar el ancho o el fondo del correo habilitaba el botón
// de deshacer, pero deshacer no lo revertía (B12).
const crearSnapshot = (form) =>
  JSON.parse(
    JSON.stringify({
      jsonMail: form.jsonMail,
      mailStyles: form.mailStyles || {},
      mailContainerStyles: form.mailContainerStyles || {},
    }),
  );

const aplicarSnapshot = (state, snapshot) => {
  const copia = JSON.parse(JSON.stringify(snapshot));
  state.form.jsonMail = copia.jsonMail;
  state.form.mailStyles = copia.mailStyles;
  state.form.mailContainerStyles = copia.mailContainerStyles;
};

const createStateFn = () => {
  const storedForm = getStoredForm();
  const form = buildFormFromStorage(storedForm);
  return {
    section: getInitialSection(),
    sections: [
      { id: 0, icon: "GearsIcon", name: "Configuración" },
      { id: 1, icon: "DesingnIcon", name: "Diseñar" },
      { id: 2, icon: "SendIcon", name: "Envío" },
    ],
    selectedModule: null,
    // Modo del canvas: "move" (estructura: arrastrar, agregar, ordenar) o
    // "edit" (contenido: escribir, estilos). Son excluyentes a propósito —
    // intentar las dos cosas a la vez es de donde salieron B1, B26 y B30.
    // Estado de UI, como `tab`: no entra en `form` ni se persiste.
    // Arranca en "move" porque la paleta vive en ese modo: con un correo
    // vacío, abrir en "edit" no dejaría nada que hacer.
    builderMode: "move",
    // Solo "tools" | "sections". Que se vea el panel de Estilos lo decide
    // `builderMode`, no este valor.
    tab: "tools",
    // Id del último bloque que apareció sin que el usuario lo soltara en el
    // canvas (clic en la paleta, duplicar). DesignBuilder lo observa para
    // llevarlo a la vista: en un correo largo cae fuera de pantalla y el clic
    // parecería no haber hecho nada. Estado de UI, como `tab`: no se persiste.
    ultimoBloqueAgregadoId: null,
    direcciones: [],
    destinatariosListas: [],
    categorias: [],
    builderElements: buildBuilderElements(form?.datosMarca),
    builderSections: buildBuilderSectionsWithBrand(form?.datosMarca),
    form,
    formImages: [],
    formActualImage: 0,
    fonts: [],
    categoriasTemplates: {},
  };
};

export const createState = { ...createStateFn() };

export const createMutations = {
  setTab(state, tab) {
    state.tab = tab;
  },
  setBuilderMode(state, mode) {
    state.builderMode = mode === "edit" ? "edit" : "move";
    // Al volver a "move" el panel muestra Herramientas/Secciones; si `tab`
    // quedó con cualquier otro valor, normalizarlo.
    if (state.builderMode === "move" && state.tab !== "sections") {
      state.tab = "tools";
    }
  },
  setCategoriasTemplates(state, categoriasTemplates) {
    state.categoriasTemplates = categoriasTemplates;
  },
  setSection(state, section) {
    state.section = section;
    localStorage.setItem("mail-create-section", JSON.stringify(section));
    persistFormToStorage(state.form);
  },

  setRemitentesEmpresa(state, remitentes) {
    const mapped = Array.isArray(remitentes)
      ? remitentes
        .filter((r) => r && r.id_remitente && r.correo_remitente)
        .map((r) => ({
          id: r.id_remitente,
          text: r.correo_remitente,
          nombre: r.nombre_remitent || r.nombre_remitente || "",
          raw: r,
        }))
      : [];

    state.direcciones = mapped.length ? mapped : state.direcciones;
  },

  setSelectedModule(state, { rowIndex, modIndex, module }) {
    state.selectedModule = { rowIndex, modIndex, module };
    // Antes forzaba `tab = "styles"`. Ya no: el panel de Estilos se muestra
    // cuando `builderMode === "edit"`. Si esto siguiera, terminar un drag
    // —que reselecciona el módulo soltado— mandaría el panel a Estilos en
    // pleno modo Mover, sacando la paleta de en medio.
  },

  clearSelectedModule(state) {
    state.selectedModule = null;
  },

  // Reemplazan, no mergean — igual que `updateModuleStyles`. Con el merge,
  // quitar un estilo del correo era imposible: el panel mandaba el mapa sin la
  // propiedad y el spread la volvía a traer. `StylesEdit` siempre envía el mapa
  // completo (`loadModuleStyles` lo carga entero), así que `width: 600px` y
  // demás se conservan.
  updateMailStyles(state, mailStyles) {
    state.form.mailStyles = { ...(mailStyles || {}) };
    persistFormToStorage(state.form);
  },

  updateMailContainerStyles(state, mailContainerStyles) {
    state.form.mailContainerStyles = { ...(mailContainerStyles || {}) };
    persistFormToStorage(state.form);
  },

  // Las cuatro mutaciones `updateModule*` aceptan `moduleId` (preferido) o el
  // par {rowIndex, modIndex} heredado. Ver `locateModule`.
  updateModuleStyles(state, payload) {
    const found = locateModule(state, payload);
    if (!found) return;

    const nextStyles = { ...(payload.styles || {}) };
    found.module.styles = nextStyles;
    syncSelectedModule(state, found.module, { styles: nextStyles });
    persistFormToStorage(state.form);
  },

  updateModuleStylesContainer(state, payload) {
    const found = locateModule(state, payload);
    if (!found) return;

    const nextContainerStyles = { ...(payload.stylesContainer || {}) };
    found.module.stylesContainer = nextContainerStyles;
    syncSelectedModule(state, found.module, {
      stylesContainer: nextContainerStyles,
    });
    persistFormToStorage(state.form);
  },

  updateModuleContent(state, payload) {
    const found = locateModule(state, payload);
    if (!found) return;

    found.module.content = payload.content;
    syncSelectedModule(state, found.module, { content: payload.content });
    persistFormToStorage(state.form);
  },

  updateModuleAttributes(state, payload) {
    const found = locateModule(state, payload);
    if (!found) return;

    const nextAttributes = {
      ...found.module.attributes,
      ...payload.attributes,
    };
    found.module.attributes = nextAttributes;
    syncSelectedModule(state, found.module, { attributes: nextAttributes });
    persistFormToStorage(state.form);
  },

  setDestinatariosListas(state, listas = []) {
    const normalized = (listas || []).map((item) => ({
      ...item,
      id: item.id_lista || item.id,
      text: item.nombre_lista || item.text,
      tipo: item.tipo || "lista",
      // Preservar destinatarios completos para permitir expansión en modales
      destinatarios: item.destinatarios || [],
    }));
    state.destinatariosListas = normalized;
  },

  /**
   * Actualiza los destinatarios (usuarios) del formulario.
   * Reemplaza state.form para garantizar reactividad en todos los componentes.
   */
  setFormDestinatarios(state, destinatarios) {
    state.form = {
      ...state.form,
      destinatarios: Array.isArray(destinatarios) ? destinatarios : [],
    };
    persistFormToStorage(state.form);
  },

  /**
   * Actualiza las listas de destinatarios seleccionadas del formulario.
   * Reemplaza state.form para garantizar reactividad en todos los componentes.
   */
  setFormDestinatariosListas(state, listas) {
    state.form = {
      ...state.form,
      destinatariosListasSeleccionadas: Array.isArray(listas) ? listas : [],
    };
    persistFormToStorage(state.form);
  },

  setForm(state, form) {
    const normalizedRows = ensureRows(form?.jsonMail?.rows);
    const normalizedDestinatarios = Array.isArray(form?.destinatarios)
      ? form.destinatarios
      : state.form.destinatarios;
    const normalizedDestinatariosListas =
      normalizeDestinatariosListasSeleccionadas(form);
    state.form = {
      ...state.form,
      ...form,
      id_correo: form.id_correo || state.form.id_correo,
      destinatarios: normalizedDestinatarios,
      destinatariosListasSeleccionadas: normalizedDestinatariosListas,
      destinatariosLista: null,
      mailContainerStyles: {
        ...state.form.mailContainerStyles,
        ...(form?.mailContainerStyles || {}),
      },
      jsonMail: {
        ...state.form.jsonMail,
        ...(form?.jsonMail || {}),
        rows: normalizedRows,
      },
    };
    persistFormToStorage(state.form);
  },

  setJsonMailRows(state, rows) {
    const normalizedRows = ensureRows(rows);
    state.form = {
      ...state.form,
      jsonMail: {
        ...state.form.jsonMail,
        rows: normalizedRows,
      },
    };
    persistFormToStorage(state.form);
  },

  updateRowCols(state, { rowIndex, cols }) {
    const normalizedCols = (cols || [])
      .map((col) => normalizeModule(col))
      .filter(Boolean);
    const rows = state.form.jsonMail.rows.map((row, index) =>
      index === rowIndex
        ? {
          ...row,
          id: row.id || generateId(),
          cols: normalizedCols,
        }
        : row,
    );
    state.form = {
      ...state.form,
      jsonMail: {
        ...state.form.jsonMail,
        rows: ensureRows(rows),
      },
    };
    persistFormToStorage(state.form);
  },

  // ── Agregar, mover y duplicar sin arrastrar ───────────────────────────────
  // Direccionan por id (de bloque o de fila), no por posición: es la misma
  // regla que las `updateModule*`, y por eso ninguna invalida la selección, que
  // también va por id. No tocan a Sortable ni al DOM: reemplazan las filas del
  // store (`reemplazarFilas`) y el canvas se actualiza solo. Todas entran al
  // historial vía `mailHistoryPlugin`.
  //
  // `direccion` es -1 (hacia arriba o la izquierda) o 1 (hacia abajo o la
  // derecha). En el borde no hacen nada: los botones que las disparan se
  // deshabilitan antes de llegar acá, esto es solo la red de seguridad.

  // Un bloque de la paleta, en una fila nueva. `despuesDeId` es el id del bloque
  // debajo del cual va; sin él, al final. Ver `insertarFilasEn`.
  insertarBloque(state, { bloque, despuesDeId = null } = {}) {
    if (!bloque) return;
    insertarFilasEn(state, [{ id: generateId(), cols: [bloque] }], despuesDeId);
  },

  // Las filas de un diseño prearmado, con las mismas reglas que `insertarBloque`.
  insertarFilas(state, { filas, despuesDeId = null } = {}) {
    insertarFilasEn(state, filas, despuesDeId);
  },

  // B5. Un bloque soltado en la franja "debajo de esta fila" (DesignBuilder): va
  // en una FILA NUEVA justo debajo de `filaId`, en vez de colarse como columna
  // de la fila de arriba, que es lo que pasaba al soltarlo cerca de una fila con
  // contenido. `bloque` es el que viaja en el arrastre (con id nuevo: lo clonó
  // `cloneModule` o `cloneElement`).
  //
  // `origenFilaId` es la fila de la que salió el bloque (null si viene de la
  // paleta). Sortable ya lo quitó de allí cuando esto corre, y de eso salen dos
  // casos que no se pueden dejar a medias:
  //  - Lo soltó en el pie de su PROPIA fila y era su único bloque: la fila quedó
  //    vacía y "debajo de sí misma" no es un lugar distinto. El bloque vuelve a
  //    su fila y no cambia nada; si no, quedaría una fila vacía encima de la nueva.
  //  - Era el único bloque de OTRA fila: esa fila queda vacía por haberlo movido
  //    y no pinta nada (las filas vacías son las que se crean a propósito, con
  //    "Añadir fila"). Se retira.
  // Si `filaId` ya no existe, el bloque va al final: perderlo no es opción, el
  // origen ya lo sacó. Deja seleccionado el bloque, como cualquier "agregar".
  insertarBloqueDebajoDeFila(state, { bloque, filaId, origenFilaId = null } = {}) {
    if (!bloque) return;
    const rows = state.form?.jsonMail?.rows || [];
    const vacia = (row) => !row || !(row.cols && row.cols.length);
    const destino = rows.findIndex((row) => row && row.id === filaId);

    let siguientes;
    if (destino !== -1 && vacia(rows[destino])) {
      siguientes = rows.map((row, index) =>
        index === destino ? { ...row, cols: [bloque] } : row,
      );
    } else {
      siguientes = [...rows];
      siguientes.splice(destino === -1 ? rows.length : destino + 1, 0, {
        id: generateId(),
        cols: [bloque],
      });
      if (origenFilaId != null && origenFilaId !== filaId) {
        siguientes = siguientes.filter(
          (row) => !(row && row.id === origenFilaId && vacia(row)),
        );
      }
    }
    reemplazarFilas(state, siguientes);
    seleccionarBloqueNuevo(state, bloque.id);
  },

  moverFila(state, { rowId, direccion } = {}) {
    const rows = state.form?.jsonMail?.rows || [];
    const desde = rows.findIndex((row) => row && row.id === rowId);
    const hasta = desde + (direccion < 0 ? -1 : 1);
    if (desde === -1 || hasta < 0 || hasta >= rows.length) return;

    const siguientes = [...rows];
    [siguientes[desde], siguientes[hasta]] = [siguientes[hasta], siguientes[desde]];
    reemplazarFilas(state, siguientes);
  },

  // La copia queda justo debajo y con ids nuevos en todo: si repitiera el id de
  // un bloque, `ensureRows` descartaría el repetido.
  duplicarFila(state, { rowId } = {}) {
    const rows = state.form?.jsonMail?.rows || [];
    const indice = rows.findIndex((row) => row && row.id === rowId);
    if (indice === -1) return;

    const copia = clonarFila(rows[indice]);
    const siguientes = [...rows];
    siguientes.splice(indice + 1, 0, copia);
    reemplazarFilas(state, siguientes);

    const primero = (copia.cols || [])[0];
    if (primero) seleccionarBloqueNuevo(state, primero.id);
  },

  // Mueve el bloque un lugar dentro de SU fila (intercambia con el vecino). Pasar
  // a otra fila es de las filas, o del arrastre.
  moverBloque(state, { moduleId, direccion } = {}) {
    const found = locateModule(state, { moduleId });
    if (!found) return;

    const rows = state.form.jsonMail.rows;
    const cols = rows[found.rowIndex].cols;
    const hasta = found.modIndex + (direccion < 0 ? -1 : 1);
    if (hasta < 0 || hasta >= cols.length) return;

    const colsNuevas = [...cols];
    [colsNuevas[found.modIndex], colsNuevas[hasta]] = [
      colsNuevas[hasta],
      colsNuevas[found.modIndex],
    ];
    reemplazarFilas(
      state,
      rows.map((row, index) =>
        index === found.rowIndex ? { ...row, cols: colsNuevas } : row,
      ),
    );
  },

  // La copia queda a la derecha del original, en la misma fila, y pasa a ser la
  // seleccionada.
  duplicarBloque(state, { moduleId } = {}) {
    const found = locateModule(state, { moduleId });
    if (!found) return;

    const copia = { ...JSON.parse(JSON.stringify(found.module)), id: generateId() };
    const rows = state.form.jsonMail.rows;
    const colsNuevas = [...rows[found.rowIndex].cols];
    colsNuevas.splice(found.modIndex + 1, 0, copia);
    reemplazarFilas(
      state,
      rows.map((row, index) =>
        index === found.rowIndex ? { ...row, cols: colsNuevas } : row,
      ),
    );
    seleccionarBloqueNuevo(state, copia.id);
  },

  setFormImages(state, snapshot) {
    // Si estamos en medio del historial, eliminar el futuro
    if (state.formActualImage < state.formImages.length - 1) {
      state.formImages = state.formImages.slice(0, state.formActualImage + 1);
    }

    // Evitar duplicados - comparar con el último snapshot
    const lastSnapshot = state.formImages[state.formImages.length - 1];
    if (
      lastSnapshot &&
      JSON.stringify(lastSnapshot) === JSON.stringify(snapshot)
    ) {
      return; // No guardar si es idéntico al último
    }

    state.formImages.push(snapshot);

    // Limitar el historial
    if (state.formImages.length > MaxImages) {
      state.formImages.shift();
      // Ajustar el índice si se eliminó el primero
      if (state.formActualImage > 0) {
        state.formActualImage--;
      }
    } else {
      state.formActualImage = state.formImages.length - 1;
    }
  },

  setFormActualImage(state, index) {
    if (index >= 0 && index < state.formImages.length) {
      state.formActualImage = index;
      aplicarSnapshot(state, state.formImages[index]);

      // Guardar en localStorage
      persistFormToStorage(state.form);
    }
  },

  restoreFromHistory(state, index) {
    if (index >= 0 && index < state.formImages.length) {
      state.formActualImage = index;
      aplicarSnapshot(state, state.formImages[index]);

      if (state.selectedModule && state.selectedModule.rowIndex === -1) {
        // Si está seleccionado el correo completo, refrescar sus dos mapas
        state.selectedModule.module.styles = { ...state.form.mailStyles };
        state.selectedModule.module.stylesContainer = {
          ...state.form.mailContainerStyles,
        };
      } else if (state.selectedModule && state.selectedModule.module) {
        // Por id, no por posición: si el paso deshecho movió módulos, el par
        // {rowIndex, modIndex} guardado apunta a otro módulo y el panel
        // mostraba (y editaba) ése. Si el módulo no existe en el snapshot
        // (se deshizo su alta), se suelta la selección.
        const found = locateModule(state, {
          moduleId: state.selectedModule.module.id,
        });
        if (found) {
          state.selectedModule.rowIndex = found.rowIndex;
          state.selectedModule.modIndex = found.modIndex;
          state.selectedModule.module = JSON.parse(JSON.stringify(found.module));
        } else {
          state.selectedModule = null;
        }
      }

      // Antes no se persistía "para evitar guardados innecesarios", y eso
      // hacía que deshacer y recargar devolviera el cambio deshecho.
      persistFormToStorage(state.form);
    }
  },

  inicialize(state, { fonts }) {
    const sortedFonts = fonts.sort((a, b) =>
      a.nombre_tipografia.localeCompare(b.nombre_tipografia),
    );
    state.fonts = sortedFonts;
  },

  saveLocalDatosMarca(state, datosMarca) {
    state.form.datosMarca = { ...state.form.datosMarca, ...datosMarca };
    state.builderElements = buildBuilderElements(state.form.datosMarca);
    state.builderSections = buildBuilderSectionsWithBrand(
      state.form.datosMarca,
    );
    persistFormToStorage(state.form);
  },

  saveCategorias(state, categorias) {
    state.categorias = categorias;
  },

  updateAllLogos(state, url_imagen) {
    const logoIndex = state.builderElements.findIndex(
      (el) => el.name === "Logotipo",
    );
    if (logoIndex === -1) return;
    state.builderElements[logoIndex].content = url_imagen;
    for (let i = 0; i < state.form.jsonMail.rows.length; i++) {
      if (!state.form.jsonMail.rows[i].cols) continue;
      for (let ii = 0; ii < state.form.jsonMail.rows[i].cols.length; ii++) {
        if (
          state.form.jsonMail.rows[i].cols[ii].name === "Logotipo" &&
          state.form.jsonMail.rows[i].cols[ii].tag === "img"
        ) {
          state.form.jsonMail.rows[i].cols[ii].content = url_imagen;
        }
      }
    }
  },

  /**
   * Agrega un archivo adjunto al formulario de correo.
   * @param {Object} state - Estado de Vuex
   * @param {Object} attachment - Objeto del archivo adjunto
   * @param {number} attachment.id_archivo - ID del archivo en el repositorio
   * @param {string} attachment.url - URL del archivo subido
   * @param {string} attachment.nombre - Nombre del archivo
   * @param {number} attachment.size - Tamaño del archivo en bytes
   * @param {string} attachment.type - Tipo MIME del archivo
   */
  addAttachment(state, attachment) {
    if (!state.form.archivosAdjuntos) {
      state.form.archivosAdjuntos = [];
    }
    state.form.archivosAdjuntos.push(attachment);
    persistFormToStorage(state.form);
  },

  /**
   * Elimina un archivo adjunto del formulario por índice.
   * @param {Object} state - Estado de Vuex
   * @param {number} index - Índice del adjunto a eliminar
   */
  removeAttachment(state, index) {
    if (state.form.archivosAdjuntos && state.form.archivosAdjuntos[index]) {
      state.form.archivosAdjuntos.splice(index, 1);
      persistFormToStorage(state.form);
    }
  },

  /**
   * Limpia todos los archivos adjuntos del formulario.
   * @param {Object} state - Estado de Vuex
   */
  clearAttachments(state) {
    state.form.archivosAdjuntos = [];
    persistFormToStorage(state.form);
  },

  resetState(state) {
    localStorage.removeItem("mail-create-form");
    localStorage.removeItem("mail-create-section");
    Object.assign(state, createStateFn());
  },
};

export const createActions = {
  async inicialize({ dispatch, commit, state }, { idCorreo } = {}) {
    let correo = null;

    // Cargar datos base (marca, fuentes, categorias y remitentes)
    const [datosMarca, fonts, cats] = await Promise.all([
      dispatch("getDatosMarca"),
      dispatch("getFonts"),
      dispatch("getCategoies"),
    ]).catch(() => [null, [], [], []]);

    // Remitentes se cargan pero no es necesario esperar para inicializar
    dispatch("getListRemitentesEmpresa").catch(() => null);

    if (datosMarca) {
      commit("inicialize", { fonts });
      commit("saveLocalDatosMarca", datosMarca);
      commit("saveCategorias", cats || []);
      if (datosMarca.url_imagen)
        commit("updateAllLogos", datosMarca.url_imagen);
    }

    // Si hay idCorreo, obtenerlo y setear en form
    if (idCorreo) {
      correo = await exQue(FIND_CORREO_BY_ID, {
        idCorreo: Number(idCorreo),
      }).catch(() => null);

      if (correo) {
        const mapped = mapCorreoToForm(correo, state.direcciones);
        commit("setForm", mapped);
      }
    }

    return true;
  },

  preSaveLocalDatosMarca({ commit, state }, datosMarca) {
    const imageUrlChanged =
      datosMarca.url_imagen &&
      datosMarca.url_imagen !== state.form.datosMarca.url_imagen;
    commit("saveLocalDatosMarca", datosMarca);
    if (imageUrlChanged) commit("updateAllLogos", datosMarca.url_imagen);
  },

  async getDatosMarca() {
    const datos = await exQue(GET_DATOS_MARCA, {});
    return datos;
  },

  async fetchDestinatariosListas({ commit }) {
    try {
      const listas = await exQue(LIST_LISTAS_CONTACTOS, {}, true);
      commit("setDestinatariosListas", listas || []);
      return listas;
    } catch (e) {
      console.error("Error obteniendo listas de destinatarios:", e);
      commit("setDestinatariosListas", []);
      return [];
    }
  },

  async getFonts() {
    const datos = await exQue(GET_FONTS, {});
    return datos;
  },

  async getCategoies({ commit }) {
    const categorias = await exQue(GET_CATEGORIAS_MAIL, { optionAll: false });
    const safeCategorias = categorias || [];
    const templates = await Promise.all(
      safeCategorias.map((cat) =>
        exQue(GET_TEMPLATES_BY_CATEGORIA, {
          idCategoria: cat.id_categoria,
        }).then((tpls) => mapTemplatesToForm(tpls)),
      ),
    );
    const categoriasTemplates = safeCategorias.reduce((acc, cat, index) => {
      acc[cat.id_categoria] = templates[index] || [];
      return acc;
    }, {});

    commit("setCategoriasTemplates", categoriasTemplates);
    return safeCategorias;
  },

  async CreateUpdateDatosMarca({ state, dispatch }, { base64, formato }) {
    const datosMarca = state.form.datosMarca;
    const data = {
      idTipografiaHeader: datosMarca.tipo_header?.id_tipografia || 0,
      idTipografiaBody: datosMarca.tipo_body?.id_tipografia || 0,
      colores:
        datosMarca.colores.map(({ color, is_primary }) => ({
          color,
          is_primary,
        })) || [],
      imagen: {
        base64,
        formato,
      },
      idMarca: datosMarca.id_marca || null,
    };

    const datosMarcaRes = await exMut(PUT_DATOS_MARCA, data);

    // Usar preSaveLocalDatosMarca para manejar la actualización de logos automáticamente
    dispatch("preSaveLocalDatosMarca", datosMarcaRes);

    return datosMarcaRes;
  },

  async getListRemitentesEmpresa({ commit }) {
    const remitentes = await exQue(GET_LIST_REMITENTES_EMPRESA);
    commit("setRemitentesEmpresa", remitentes || []);
  },

  async saveDraft(
    { state },
    { isTemplate = false, isDraft = true } = { isTemplate: false, isDraft: true },
  ) {
    const form = state.form || {};
    const destinatarios = mapDestinatariosInput(form);
    const fechaProgramada = form.programar
      ? formatDateString(form.fecha)
      : null;

    // Los tres modos de SaveCorreo son excluyentes entre sí:
    //   borrador  → is_eraser: true,  is_programmed: false
    //   programar → is_eraser: false, is_programmed: true
    //   enviar    → is_eraser: false, is_programmed: false
    // Antes se mandaba is_eraser Y is_programmed en `true` al programar —una
    // combinación que el contrato no define—, así que el correo quedaba como
    // borrador y el cron no lo tomaba nunca. `is_template` es ortogonal, pero
    // guardar una plantilla tampoco debe disparar un envío.
    const guardarSinEnviar = Boolean(isDraft) || Boolean(isTemplate);
    const programarEnvio =
      !guardarSinEnviar && Boolean(form.programar) && !form.automatizar;

    // Mapear archivos adjuntos al formato esperado por el backend (FileAdjuntosInput)
    const archivosAdjuntos = (form.archivosAdjuntos || [])
      .filter((att) => att?.url && (att.id_archivo || att.id_adjunto))
      .map((att) => ({
        id_adjunto: Number(att.id_archivo || att.id_adjunto) || null,
        url_adjunto: att.url,
        nombre_adjunto: att.nombre || "",
        size: att.size || 0,
        formato: att.type || "",
      }));
    const contenidoHtml = renderHtmlFromBuilderJson(form) || form.html || "";

    // Sin `var_dinamicas: true` el backend no reemplaza los {{placeholders}} —salen
    // literales al destinatario— y además manda a todos en el mismo `to`, con lo
    // que se ven entre ellos. Se activa solo si el correo realmente usa alguno.
    const varDinamicas = /\{\{\s*[\w.]+\s*\}\}/.test(contenidoHtml);

    const payload = {
      idCorreo: form.id_correo || form.idCorreo || 0,
      idRemitente: form.direccion?.id || form.direccion?.value || null,
      nombreRemitente: form.remitente || form.direccion?.text || "",
      asunto: form.asunto || "(Sin asunto)",
      introduccion: form.introduccion || form.itroduccion || "",
      archivosAdjuntos,
      destinatarios,
      contenidoHtml,
      contenidoJson: JSON.stringify(form.jsonMail || {}),
      varDinamicas,
      isTemplate,
      isProgrammed: programarEnvio,
      isEraser: guardarSinEnviar,
      fechaProgramada,
      // La fecha/hora viajan siempre que el usuario haya elegido programar, aunque
      // esté guardando un borrador: así el borrador recuerda su programación.
      horaProgramada:
        form.programar && !form.automatizar
          ? formatTimeString(form.hora)
          : null,
      // TODO: Backend dev — agregar estos campos al schema de SAVE_MAIL cuando esté listo
      // isAutomatizado: Boolean(form.automatizar),
      // automatizacion: form.automatizar ? {
      //   idPeriodo: form.automatizacion?.id_periodo,
      //   diasEjecuciones: form.automatizacion?.dias_ejecuciones,
      //   fechasEjecuciones: form.automatizacion?.fechas_ejecuciones,
      //   horaEjecucion: form.automatizacion?.hora,
      // } : null,
      urlImagenTemplate:
        "https://storage.googleapis.com/bucket-mailing-c3/8375bm_cpts/templates_thumbnails/template_1759940117685.png" /* Todo: hay que poner una por defeto por ahora */,
      formatoImagenTemplate: form.formato_imagen_template || null,
      idCategoriaFk: form.id_categoria_fk || null,
    };

    if (!payload.idRemitente) {
      throw new Error("Debe seleccionar una direccion de remitente");
    }
    if (!payload.nombreRemitente) {
      throw new Error("Debe ingresar un nombre de remitente");
    }

    const response = await exMut(SAVE_MAIL, payload);

    // Persist returned id_correo into local form to allow subsequent updates/redirects
    if (response && response.id_correo) {
      state.form.id_correo = response.id_correo;
      persistFormToStorage(state.form);
    }
    return response;
  },

  async createAutomatedTemplate({ state, dispatch }) {
    const form = state.form || {};
    const auto = form.automatizacion || {};

    if (!form.direccion?.id && !form.direccion?.value) {
      throw new Error("Debe seleccionar una dirección de remitente");
    }
    if (!form.remitente && !form.direccion?.text) {
      throw new Error("Debe ingresar un nombre de remitente");
    }
    if (!auto.id_periodo) {
      throw new Error("Debe seleccionar un tipo de recurrencia");
    }
    if (!auto.hora) {
      throw new Error("Debe ingresar una hora de envío");
    }

    // Validar ejecución según el tipo de periodo (mismo patrón que EPP)
    const periodosConDias = [1, 3]; // Semanal, Mensual
    const periodosConFechas = [7];  // Anual/Fechas
    if (periodosConDias.includes(auto.id_periodo)) {
      const dia = auto.dias_ejecuciones?.[0];
      if (dia == null) {
        throw new Error("Debe seleccionar el día de ejecución");
      }
    }
    if (periodosConFechas.includes(auto.id_periodo)) {
      if (!auto.fechas_ejecuciones?.length) {
        throw new Error("Debe seleccionar al menos una fecha de ejecución");
      }
    }

    const destinatarios = mapDestinatariosInput(form);

    // Sin destinatarios el backend envía a TODA la empresa, y acá se repetiría
    // en cada ejecución de la recurrencia.
    if (
      !destinatarios.destinatarios_usuarios.length &&
      !destinatarios.destinatarios_listas.length
    ) {
      throw new Error(
        "Debe seleccionar al menos un destinatario o una lista de contactos"
      );
    }

    const archivosAdjuntos = (form.archivosAdjuntos || [])
      .filter((att) => att?.url && (att.id_archivo || att.id_adjunto))
      .map((att) => ({
        id_adjunto: Number(att.id_archivo || att.id_adjunto) || null,
        url_adjunto: att.url,
        nombre_adjunto: att.nombre || "",
        size: att.size || 0,
        formato: att.type || "",
      }));

    // fechaEnvio: última fecha del arreglo (modo Fechas), o vacío para otros periodos
    const fechaEnvioArr = auto.fechas_ejecuciones || [];
    const fechaEnvio = fechaEnvioArr.length
      ? fechaEnvioArr[fechaEnvioArr.length - 1]
      : "";

    const payload = {
      asunto: form.asunto || "(Sin asunto)",
      introduccion: form.introduccion || form.itroduccion || "",
      idRemitente: form.direccion?.id || form.direccion?.value || null,
      urlImagen: form.datosMarca?.url_imagen || "",
      formatoImagen: form.formato_imagen_template || "",
      idCategoriaFk: form.id_categoria_fk || null,
      contenidoHtml: renderHtmlFromBuilderJson(form) || form.htmlMail || "",
      contenidoJson: JSON.stringify(form.jsonMail || {}),
      isAutomatico: true,
      byIngreso: Boolean(auto.by_ingreso),
      byNacimiento: Boolean(auto.by_nacimiento),
      cadenciaEnvio: auto.id_periodo || null,
      isActive: true,
      diaEnvio: auto.dias_ejecuciones?.[0] ?? null,
      fechaEnvio,
      horaEnvio: auto.hora ? auto.hora + ':00' : "",
      destinatarios,
      archivosAdjuntos,
    };

    const response = await exMut(
      CREATE_AUTOMATED_TEMPLATE,
      payload,
      "Template automatizado creado exitosamente.",
      "No fue posible crear el template automatizado."
    );

    // Refrescar lista de templates por categoría
    if (response) {
      dispatch("getCategorias");
    }

    return response;
  },

  resetState({ commit }) {
    commit("resetState");
  },

  imageBack({ commit, state }) {
    if (state.formActualImage > 0) {
      commit("restoreFromHistory", state.formActualImage - 1);
    }
  },

  imageFront({ commit, state }) {
    if (state.formActualImage < state.formImages.length - 1) {
      commit("restoreFromHistory", state.formActualImage + 1);
    }
  },

  saveHistory({ commit, state }) {
    commit("setFormImages", crearSnapshot(state.form));
  },

  async uploadFileSubscription(_, args) {
    const {
      id_empresa,
      id_usuario,
      tipo_archivo,
      file,
      descripcion,
      inputUri = "",
      filenameInputUri = "",
      fileName = "",
      tipoContenido = "adjunto",
    } = args;

    const duration = args.duration
      ? new Date(args.duration * 1000).toISOString().slice(11, 19)
      : file?.duration
        ? new Date(file.duration * 1000).toISOString().slice(11, 19)
        : "00:00:00";

    const variables = {
      idEmpresa: id_empresa,
      idUsuario: id_usuario,
      descripcionArchivo: descripcion || "",
      tiempo: String(duration),
      tipoArchivo: Number(tipo_archivo),
      nombreArchivo: file?.name || filenameInputUri || fileName || "",
      archivoB64: file?.src || "",
      formatoB64: file?.type || "",
      inputUri,
      filenameInputUri,
      fileName,
      tipoContenido,
    };

    const payload = {
      query: SAVE_FILE_MAIL_BUILDER_BODY,
      variables,
    };
    return apolloClient.subscribe(payload);
  },
};

export const createGetters = {
  /**
   * Total real de destinatarios, expandiendo los usuarios de cada lista y
   * descontando los excluidos. Vive en el store porque lo necesitan tanto el
   * resumen de `Config.vue` como la validación previa al envío (`Crear.vue`):
   * con dos implementaciones, el número del aviso podría no ser el que se envía.
   */
  totalDestinatariosExpandidos(state) {
    const form = state.form || {};
    const seenIds = new Set();
    let count = 0;

    (form.destinatarios || []).forEach((u) => {
      const id = u.id || u.value || u.correo;
      if (id && !seenIds.has(id)) {
        seenIds.add(id);
        count++;
      }
    });

    const listasSeleccionadas = form.destinatariosListasSeleccionadas || [];
    let listasDisponibles = [];
    if (Array.isArray(state.destinatariosListas)) {
      listasDisponibles = state.destinatariosListas;
    } else if (
      state.destinatariosListas &&
      typeof state.destinatariosListas === "object"
    ) {
      listasDisponibles = Object.values(state.destinatariosListas).flat();
    }

    listasSeleccionadas.forEach((sel) => {
      const idLista = sel.id_lista || sel.id || sel.value;
      const lista = listasDisponibles.find(
        (l) => (l.id_lista || l.id) === idLista
      );
      if (!lista || !Array.isArray(lista.destinatarios)) return;

      const excluded = sel.correos_excluidos || [];
      lista.destinatarios.forEach((dest) => {
        if (!dest.datos || !Array.isArray(dest.datos.lista)) return;
        dest.datos.lista.forEach((u) => {
          if (u.id_usuario && !seenIds.has(u.id_usuario)) {
            seenIds.add(u.id_usuario);
            if (
              !excluded.includes(u.id_usuario) &&
              !excluded.includes(u.correo)
            ) {
              count++;
            }
          }
        });
      });
    });

    return count;
  },

  /**
   * ¿Hay al menos un destinatario elegido? Con ambos arreglos vacíos el backend
   * envía a TODA la empresa, así que esto es una guarda de seguridad, no una
   * validación de formulario.
   */
  tieneDestinatarios(state) {
    const form = state.form || {};
    const usuarios = Array.isArray(form.destinatarios)
      ? form.destinatarios.length
      : 0;
    const listas = Array.isArray(form.destinatariosListasSeleccionadas)
      ? form.destinatariosListasSeleccionadas.length
      : 0;
    return usuarios + listas > 0;
  },

  // Dónde está un bloque dentro de su fila: `{ modIndex, total }`, o null si no
  // está en el correo (por ejemplo, un instante mientras Sortable lo traslada).
  // La barra de acciones del bloque decide con esto si ofrece mover a los lados
  // y cuál de las dos flechas queda deshabilitada.
  posicionBloque(state) {
    return (moduleId) => {
      const found = locateModule(state, { moduleId });
      if (!found) return null;
      return {
        modIndex: found.modIndex,
        total: state.form.jsonMail.rows[found.rowIndex].cols.length,
      };
    };
  },

  // Id del bloque seleccionado, o null. El correo entero (`rowIndex === -1`, ver
  // `selectMail`) no es un bloque: no sirve de ancla para agregar debajo.
  bloqueSeleccionadoId(state) {
    const seleccion = state.selectedModule;
    if (!seleccion || seleccion.rowIndex === -1 || !seleccion.module) return null;
    return seleccion.module.id ?? null;
  },

  backButtonEnabled(state) {
    if (state.formActualImage === 0 || state.formImages.length === 0)
      return false;
    return true;
  },

  frontButtonEnabled(state) {
    if (state.formImages.length === 0) return false;
    if (state.formActualImage === state.formImages.length - 1) return false;
    return true;
  },

  sabeDraftDisabled(state) {
    const arrayChecks = [
      Boolean(typeof state.form.direccion === "object" && state.form.remitente),
      Boolean(state.form.asunto && state.form.id_categoria_fk),
      Boolean(
        (Array.isArray(state.form.destinatarios) &&
          state.form.destinatarios.length > 0) ||
        (Array.isArray(state.form.destinatariosListasSeleccionadas) &&
          state.form.destinatariosListasSeleccionadas.length > 0),
      ),
      Boolean(
        state.form.jsonMail &&
        Array.isArray(state.form.jsonMail.rows) &&
        state.form.jsonMail.rows.some(
          (row) => Array.isArray(row.cols) && row.cols.length > 0,
        ),
      ),
    ];
    return arrayChecks.includes(false);
  },
};

export const mailHistoryPlugin = (store) => {
  // Lista de mutaciones que deben guardar historial
  const historyMutations = [
    "mailsModule/setJsonMailRows",
    "mailsModule/updateRowCols",
    "mailsModule/updateModuleStyles",
    "mailsModule/updateModuleStylesContainer",
    "mailsModule/updateModuleContent",
    "mailsModule/updateModuleAttributes",
    "mailsModule/updateMailStyles",
    "mailsModule/updateMailContainerStyles",
    // Cargar un template o un borrador reemplaza el diseño entero: tiene que
    // ser un paso deshacible. Sin esto, el último snapshot seguía siendo el
    // diseño anterior y deshacer saltaba un paso atrás de ése.
    "mailsModule/setForm",
    // Agregar, mover y duplicar sin arrastrar (cambian la estructura, igual
    // que `setJsonMailRows` cuando el cambio viene de un drag).
    "mailsModule/insertarBloque",
    "mailsModule/insertarFilas",
    "mailsModule/insertarBloqueDebajoDeFila",
    "mailsModule/moverFila",
    "mailsModule/duplicarFila",
    "mailsModule/moverBloque",
    "mailsModule/duplicarBloque",
  ];

  // Flag para evitar bucles cuando restauramos desde el historial
  let isRestoringFromHistory = false;

  store.subscribe((mutation) => {
    // Si estamos restaurando, no guardar historial
    if (mutation.type === "mailsModule/restoreFromHistory") {
      isRestoringFromHistory = true;
      setTimeout(() => {
        isRestoringFromHistory = false;
      }, 100);
      return;
    }

    // Si una mutación de historial ocurre mientras restauramos, ignorar
    if (isRestoringFromHistory) {
      return;
    }

    if (historyMutations.includes(mutation.type)) {
      // Debounce para evitar múltiples guardados en rápida sucesión
      clearTimeout(store._historyTimeout);
      store._historyTimeout = setTimeout(() => {
        store.dispatch("mailsModule/saveHistory");
      }, 300); // 300ms de debounce
    }
  });
};
